import { createPortal } from "react-dom";

interface OptionsMenuPortalProps {
  isMobile: boolean;
  showArchivedPlaces: boolean;
  optionsMenuRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onToggleArchived: () => void;
  onArchiveModule: () => void;
}

/**
 * The "Module Options" modal, portaled to document.body. The render guard and
 * action handlers (incl. setShowOptionsMenu side effects) stay in the
 * orchestrator; this leaf just renders and fires close/toggle/archive callbacks.
 */
export function OptionsMenuPortal({
  isMobile,
  showArchivedPlaces,
  optionsMenuRef,
  onClose,
  onToggleArchived,
  onArchiveModule,
}: OptionsMenuPortalProps) {
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "15vh",
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        ref={optionsMenuRef}
        onClick={(e) => e.stopPropagation()}
        className="xv-scroll"
        style={{
          backgroundColor: "var(--color-surface)",
          borderRadius: "12px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          width: isMobile ? "calc(100% - 32px)" : "340px",
          height: "auto",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--color-border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: "16px",
              fontWeight: 500,
              color: "var(--color-text)",
            }}
          >
            Module Options
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: "var(--color-text-muted)",
              fontSize: "20px",
              lineHeight: "1",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div style={{ padding: "12px 20px" }}>
          <div
            style={{
              fontSize: "11px",
              fontWeight: 500,
              color: "var(--color-text-faint)",
              marginBottom: "10px",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Actions
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            <button
              onClick={onToggleArchived}
              style={{
                width: "100%",
                padding: "10px 12px",
                background: showArchivedPlaces
                  ? "rgba(var(--color-primary-rgb), 0.12)"
                  : "none",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                color: showArchivedPlaces
                  ? "var(--color-primary-hover)"
                  : "var(--color-text-subtle)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = showArchivedPlaces
                  ? "rgba(var(--color-primary-rgb), 0.18)"
                  : "var(--color-surface-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = showArchivedPlaces
                  ? "rgba(var(--color-primary-rgb), 0.12)"
                  : "transparent")
              }
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {showArchivedPlaces ? "Exit Archive" : "View Archived"}
            </button>
            <button
              onClick={onArchiveModule}
              style={{
                width: "100%",
                padding: "10px 12px",
                background: "none",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                color: "var(--color-text-subtle)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  "var(--color-surface-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              Archive Module
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
